/**
 * Admin Plans page — the design handoff redesign
 * (`design_handoff_admin_plans/README.md`, artboard 1a), in two slices.
 *
 * Slice 1 rebuilt the page shell, the Stripe-issues card, the plans list
 * and the à la carte add-ons card. Slice 2 (this one) replaces the old
 * Edit packaging / Link Stripe Price / Subscribers / Add-on Stripe price
 * dialogs with the §4 editor drawer (`PlanEditorDrawer`, in
 * `../components/admin`). The drawer owns its own draft state, its Stripe
 * prices query and its save mutations; this page still owns the plan list,
 * the add-ons card, the row-level quick actions the design's §2 "More" menu
 * keeps outside the drawer (Unlink Stripe price, Sync display price,
 * Delete), and the dialogs the handoff says to keep unchanged (Sync from
 * pricing sheet confirm, Import from Stripe preview, Move subscribers).
 */
import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Stack,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh,
  CloudDownload,
  Sync,
  KeyboardArrowDown,
  WarningAmberRounded,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BillingPlan } from '../types/billing';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { ConfirmDialog, FormDialog, InlineMessage, PageContainer, PageHeader, ResponsiveRows, SectionCard, StatusChip } from '../components/ui';
import type { ResponsiveRowsColumn } from '../components/ui/ResponsiveRows';
import { tokens } from '../theme/tokens';
import {
  PlanEditorDrawer,
  AddonPriceTarget,
  AddonDrawerSummary,
  BillingIntervalLabel,
  DrawerTab,
  PlanSubscriberRow,
  cadenceSuffix,
  shortPriceId,
  formatAddonMoney,
  availabilitySentence,
  isFreePreviewPlan,
} from '../components/admin/PlanEditorDrawer';
import { PlanSyncPreview, PlanSyncPreviewResult, stackLabel } from '../components/admin/PlanSyncPreview';

interface StripeImportRow {
  stripePriceId: string;
  productName: string;
  amount: number;
  currency: string;
  kind: 'plan' | 'setup_fee' | 'addon';
  planId: string | null;
  planName: string | null;
  target: string;
  applied: boolean;
  note?: string;
}

/** `GET /billing/admin/plan-addons` — one a la carte add-on plus its linked Stripe Price, if any. */
interface AdminPlanAddonRow {
  key: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: BillingIntervalLabel;
  availableOnTiers: string[];
  includedOnTiers: string[];
  unlocks?: string;
  unlocksLevel?: { key: string; level: string };
  comingSoon?: boolean;
  partiallyLive?: boolean;
  stripePriceId?: string;
}

/** `GET /billing/admin/integration-addon` — the additional-integration add-on plus its linked Stripe Price, if any. */
interface AdminIntegrationAddonRow {
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: BillingIntervalLabel;
  availableOnTiers: string[];
  includedOnTiers: string[];
  stripePriceId?: string;
}

interface StripeImportResult {
  applied: boolean;
  rows: StripeImportRow[];
  unmatched: Array<{ stripePriceId: string; productName: string; reason: string }>;
}

/** The drawer's target: a plan (by id) or an add-on (by key, or the
 * `'integration'` sentinel for the additional-integration add-on). */
type DrawerState = { mode: 'plan'; planId: string; tab: DrawerTab } | { mode: 'addon'; addonId: string; tab: DrawerTab } | null;

export const AdminPlansPage: React.FC = () => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
  const [deletePlan, setDeletePlan] = useState<BillingPlan | null>(null);
  const [moveFrom, setMoveFrom] = useState<BillingPlan | null>(null);
  const [moveTo, setMoveTo] = useState('');
  const [moveNotice, setMoveNotice] = useState<string | null>(null);
  const [stripeImport, setStripeImport] = useState<StripeImportResult | null>(null);
  // §2 More menu (per plan row) and the "Unlink Stripe price" confirm it
  // opens. The design handoff keeps both as row-level quick actions outside
  // the drawer (§2's menu table still lists them), so this state — and
  // Finding 7's "stays open until the mutation settles" behavior — is
  // unchanged from slice 1.
  const [menuAnchor, setMenuAnchor] = useState<{ plan: BillingPlan; el: HTMLElement } | null>(null);
  const [unlinkPlan, setUnlinkPlan] = useState<BillingPlan | null>(null);
  // Which price the "Unlink Stripe price" confirm targets: null/undefined for
  // the plan's default price, or a `regionalPrices` key (e.g. "CA") for a
  // regional one — the two clear different fields on the backend, so the
  // confirm copy and the DELETE body both need to know which.
  const [unlinkRegion, setUnlinkRegion] = useState<string | null>(null);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const addonsCardRef = useRef<HTMLDivElement | null>(null);

  const { data: plans, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-billing-plans'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/plans', { headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to load plans');
      }
      const result = await res.json();
      return (result.data || []) as BillingPlan[];
    },
  });

  const subscriptionsQuery = useQuery({
    queryKey: ['admin-plan-subscriptions'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/plan-subscriptions', { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Could not load subscriptions');
      return body.data as Record<string, PlanSubscriberRow[]>;
    },
    staleTime: 60 * 1000,
  });
  const subscribersFor = (planId: string) => subscriptionsQuery.data?.[planId] || [];
  // Stores this plan currently governs — the same count Billing and the
  // Dashboard use (MOT-85). Canceled, superseded and non-store records stay
  // in the Subscribers tab list but are not counted here.
  const storeCountFor = (planId: string) => subscribersFor(planId).filter((row) => row.counted).length;

  const planAddonsQuery = useQuery({
    queryKey: ['admin-plan-addons'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/plan-addons', { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Could not load add-ons');
      return body.data as AdminPlanAddonRow[];
    },
  });

  const integrationAddonQuery = useQuery({
    queryKey: ['admin-integration-addon'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/integration-addon', { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Could not load the integration add-on');
      return body.data as AdminIntegrationAddonRow;
    },
  });

  // §4 drawer openers.
  const openPackaging = (plan: BillingPlan) => setDrawer({ mode: 'plan', planId: plan.id, tab: 'packaging' });
  const openStripeTab = (plan: BillingPlan) => setDrawer({ mode: 'plan', planId: plan.id, tab: 'stripe' });
  const openSubscribersTab = (plan: BillingPlan) => setDrawer({ mode: 'plan', planId: plan.id, tab: 'subs' });
  const openAddonDrawer = (target: AddonPriceTarget) =>
    setDrawer({ mode: 'addon', addonId: target.kind === 'addon' ? target.key : 'integration', tab: 'stripe' });

  const syncPreviewQuery = useQuery({
    queryKey: ['admin-plan-sync-preview'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/plans/sync-defaults/preview', { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Could not load the sync preview');
      return body.data as PlanSyncPreviewResult;
    },
    enabled: confirmSyncOpen,
    staleTime: 0,
    gcTime: 0,
  });
  // Only a preview fetched for this opening may enable Apply: a cached one
  // can predate plan edits made since.
  const syncPreviewCurrent = !syncPreviewQuery.isFetching && !syncPreviewQuery.isError;
  const syncPreview = syncPreviewCurrent ? syncPreviewQuery.data : undefined;

  const syncDefaultsMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/plans/sync-defaults', { method: 'POST', headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Sync failed');
      return body;
    },
    onSuccess: (body) => {
      queryClient.invalidateQueries({ queryKey: ['admin-billing-plans'] });
      queryClient.removeQueries({ queryKey: ['admin-plan-sync-preview'] });
      setConfirmSyncOpen(false);
      setActionMessage(
        `${body.message || 'Plans synced from defaults'}${syncPreview ? ` on the ${stackLabel(syncPreview.stack)} (${syncPreview.stack.projectId})` : ''}`,
      );
    },
  });

  const deletePlanMutation = useMutation({
    mutationFn: async (plan: BillingPlan) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/plans/${plan.id}`, { method: 'DELETE', headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Failed to delete plan');
      return body;
    },
    onSuccess: (body) => {
      setActionMessage(body.message || 'Plan deleted');
      setDeletePlan(null);
      queryClient.invalidateQueries({ queryKey: ['admin-billing-plans'] });
    },
    onError: (err: Error, plan) => {
      setDeletePlan(null);
      if (/subscription/i.test(err.message)) {
        // Offer to move the subscribers instead of just reporting the refusal.
        setMoveNotice(err.message);
        setMoveTo('');
        setMoveFrom(plan);
      } else {
        setActionMessage(err.message);
      }
    },
  });

  const moveSubscriptionsMutation = useMutation({
    mutationFn: async () => {
      if (!moveFrom || !moveTo) throw new Error('Pick a plan to move subscribers to');
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/plans/${moveFrom.id}/move-subscriptions`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ toPlanId: moveTo }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Failed to move subscriptions');
      return body as { message?: string; data: { moved: number; skippedLiveStripe: string[] } };
    },
    onSuccess: (body) => {
      setActionMessage(body.message || 'Subscriptions moved');
      setMoveFrom(null);
      queryClient.invalidateQueries({ queryKey: ['admin-billing-plans'] });
      queryClient.invalidateQueries({ queryKey: ['admin-plan-subscriptions'] });
    },
    onError: (err: Error) => setMoveNotice(err.message),
  });

  const stripeImportMutation = useMutation({
    mutationFn: async (apply: boolean) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/plans/import-stripe', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ apply }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Stripe import failed');
      return body as { data: StripeImportResult; message?: string };
    },
    onSuccess: (body) => {
      setStripeImport(body.data);
      if (body.data.applied) {
        queryClient.invalidateQueries({ queryKey: ['admin-billing-plans'] });
        setActionMessage(body.message || 'Stripe prices linked');
      }
    },
    onError: (err: Error) => setActionMessage(err.message),
  });

  const unlinkStripeMutation = useMutation({
    mutationFn: async (vars: { plan: BillingPlan; region?: string | null }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/plans/${vars.plan.id}/stripe-price`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(vars.region ? { region: vars.region } : {}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || 'Failed to unlink Stripe price');
      }
      return body;
    },
    onSuccess: (body) => {
      setActionMessage(body.message || 'Stripe price unlinked');
      setUnlinkPlan(null);
      setUnlinkRegion(null);
      setUnlinkError(null);
      queryClient.invalidateQueries({ queryKey: ['admin-billing-plans'] });
    },
    // Keep the dialog open on failure and the `loading` spinner instead of
    // closing immediately and losing the error (PR 402, Finding 7).
    onError: (err: Error) => setUnlinkError(err.message),
  });

  const syncStripeMutation = useMutation({
    mutationFn: async (plan: BillingPlan) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/plans/${plan.id}/sync-stripe-price`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || 'Failed to sync from Stripe');
      }
      return body;
    },
    onSuccess: (body) => {
      setActionMessage(body.message || 'Synced from Stripe');
      queryClient.invalidateQueries({ queryKey: ['admin-billing-plans'] });
    },
    onError: (err: Error) => setActionMessage(err.message),
  });

  const formatMoney = (plan: BillingPlan) => {
    const currency = plan.currency || 'USD';
    const amount = typeof plan.price === 'number' ? plan.price : 0;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    } catch {
      return `$${amount}`;
    }
  };

  // §2 Limits cell — "Never render -1 anywhere in the UI."
  const formatLimitNumber = (n: number | undefined | null): string => {
    if (n === undefined || n === null) return '—';
    if (n === -1) return 'Unlimited';
    if (n >= 1000) {
      const thousands = n / 1000;
      return `${Number.isInteger(thousands) ? thousands : Number(thousands.toFixed(1))}k`;
    }
    return String(n);
  };

  /**
   * The same five limits on every plan, so cards compare. Mirrors the server's
   * `normalizePlanLimits` defaults for a key an older plan doc lacks. Product
   * and brand caps apply only while `productSyncCap` is true, so any other plan
   * reads "Unlimited" for them.
   */
  const limitChipsFor = (plan: BillingPlan): Array<{ label: string; value: string }> => {
    const limits = plan.limits;
    const capOn = limits?.productSyncCap === true;
    return [
      { label: 'Users', value: formatLimitNumber(limits?.maxUsers ?? 1) },
      { label: 'API / mo', value: formatLimitNumber(limits?.maxApiCalls ?? 100) },
      { label: 'Integrations', value: formatLimitNumber(limits?.maxIntegrations ?? 0) },
      { label: 'Products', value: formatLimitNumber(capOn ? limits?.maxDataSync ?? -1 : -1) },
      { label: 'Brands', value: formatLimitNumber(capOn ? limits?.maxSyncBrands ?? -1 : -1) },
    ];
  };

  /** "Popular" is earned: the single plan with the most paying stores, never a stored flag. */
  const popularPlanId = (() => {
    let best: { id: string; count: number } | null = null;
    for (const p of plans || []) {
      const count = storeCountFor(p.id);
      if (count > 0 && (!best || count > best.count)) best = { id: p.id, count };
    }
    return best?.id ?? null;
  })();

  // §2 Price cell.
  const isFreeDisplay = (plan: BillingPlan) => plan.price === 0 && !plan.stripePriceId;
  const setupFeeSubLine = (plan: BillingPlan): string => {
    if (plan.setupFeeCustom) return 'Setup: custom quote';
    if (typeof plan.setupFee === 'number' && plan.setupFee > 0) {
      return `+ $${plan.setupFee.toLocaleString('en-US')} setup`;
    }
    return 'No setup fee';
  };

  // §2 Stripe cell.
  type StripePillTone = 'positive' | 'caution' | 'neutral';
  const stripeStatusFor = (plan: BillingPlan): { label: string; tone: StripePillTone; note: string } => {
    if (plan.stripePriceId) return { label: 'Linked', tone: 'positive', note: shortPriceId(plan.stripePriceId) };
    if (plan.price === 0) return { label: 'No charge', tone: 'neutral', note: 'Free plans skip Stripe' };
    return { label: 'Not linked', tone: 'caution', note: "Stores can't subscribe" };
  };
  /** Regional Stripe prices actually linked (a plan usually has zero or one). */
  const linkedRegionalPrices = (plan: BillingPlan): Array<{ region: string; stripePriceId: string }> =>
    Object.entries(plan.regionalPrices || {})
      .filter(([, rp]) => !!rp.stripePriceId)
      .map(([region, rp]) => ({ region, stripePriceId: rp.stripePriceId as string }));
  const setupFeePillFor = (plan: BillingPlan): { label: string; tone: StripePillTone } | null => {
    if (plan.setupFeeCustom) return { label: 'Setup: custom quote', tone: 'neutral' };
    if (typeof plan.setupFee === 'number' && plan.setupFee > 0) {
      return plan.setupFeeStripePriceId
        ? { label: 'Setup fee linked', tone: 'positive' }
        : { label: 'Setup fee not linked', tone: 'caution' };
    }
    return null;
  };

  // §3 the plan add-ons plus the additional-integration add-on, one shared row shape.
  interface AddonRow extends AddonDrawerSummary {
    name: string;
    description: string;
    currency: string;
    interval: BillingIntervalLabel;
    partiallyLive?: boolean;
    /**
     * Whether this add-on grants a real capability. A plan add-on needs
     * `unlocks` or `unlocksLevel`; the additional-integration add-on is a
     * built-in mechanism with no `PlanLimits` key of its own (it feeds
     * `integrationAddonBillingService` directly), so it's always mapped.
     */
    mapped: boolean;
  }
  const addonRows: AddonRow[] = [
    ...(planAddonsQuery.data || []).map(a => ({
      target: { kind: 'addon' as const, key: a.key, name: a.name, currency: a.currency, interval: a.interval },
      name: a.name,
      description: a.description,
      price: a.price,
      currency: a.currency,
      interval: a.interval,
      availableOnTiers: a.availableOnTiers,
      includedOnTiers: a.includedOnTiers,
      stripePriceId: a.stripePriceId,
      comingSoon: a.comingSoon,
      partiallyLive: a.partiallyLive,
      mapped: Boolean(a.unlocks || a.unlocksLevel),
    })),
    ...(integrationAddonQuery.data
      ? [
          {
            target: {
              kind: 'integration' as const,
              name: integrationAddonQuery.data.name,
              currency: integrationAddonQuery.data.currency,
              interval: integrationAddonQuery.data.interval,
            },
            name: integrationAddonQuery.data.name,
            description: integrationAddonQuery.data.description,
            price: integrationAddonQuery.data.price,
            currency: integrationAddonQuery.data.currency,
            interval: integrationAddonQuery.data.interval,
            availableOnTiers: integrationAddonQuery.data.availableOnTiers,
            includedOnTiers: integrationAddonQuery.data.includedOnTiers,
            stripePriceId: integrationAddonQuery.data.stripePriceId,
            mapped: true,
          },
        ]
      : []),
  ];
  // Mirrors `planAddonPurchaseService`'s `purchasable` computation, minus the
  // per-org terms (`available` / `purchased` / `unlockAlreadyOn`) this admin
  // view has no organization to evaluate against. Keeping this in lockstep
  // with the backend (rather than re-deriving "ready" from `stripePriceId`
  // alone) is what stops a coming-soon add-on with a price linked from
  // reading as purchasable here when `purchaseAddon` would still 400 it.
  const purchasableNow = (a: AddonRow) => Boolean(a.stripePriceId) && a.mapped && a.comingSoon !== true;
  // Three mutually exclusive, exhaustive buckets — every row lands in exactly
  // one, so "Ready to bill" and "Coming soon" can never both claim it.
  const readyAddons = addonRows.filter(purchasableNow);
  const comingSoonAddons = addonRows.filter(a => a.comingSoon === true);
  const needsPriceAddons = addonRows.filter(a => !purchasableNow(a) && a.comingSoon !== true);
  const addonGroups: Array<{ key: string; title: string; dot: string; note: string; rows: AddonRow[] }> = [
    { key: 'ready', title: 'Ready to bill', dot: tokens.signal.positive.solid, note: 'Stores can buy these now.', rows: readyAddons },
    {
      key: 'needs',
      title: 'Needs a Stripe price',
      dot: tokens.signal.caution.solid,
      note: "Priced on the sheet, but stores can't buy them until a Stripe price is linked and it's mapped to a capability.",
      rows: needsPriceAddons,
    },
    {
      key: 'soon',
      title: 'Coming soon',
      dot: tokens.ink.faint,
      note: 'Hidden from stores. Linking a price now does not make it purchasable — the capability itself is not deliverable yet.',
      rows: comingSoonAddons,
    },
  ].filter(g => g.rows.length > 0);

  // §1 Stripe issues.
  const paidPlansMissingPrice = (plans || []).filter(p => p.price > 0 && !p.stripePriceId);
  const plansMissingSetupFeePrice = (plans || []).filter(
    p => typeof p.setupFee === 'number' && p.setupFee > 0 && !p.setupFeeCustom && !p.setupFeeStripePriceId,
  );
  const addonsMissingPriceCount = needsPriceAddons.length;
  const issueCount =
    paidPlansMissingPrice.length + plansMissingSetupFeePrice.length + (addonsMissingPriceCount > 0 ? 1 : 0);

  const scrollToAddons = () => addonsCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const softBlueButtonSx = {
    bgcolor: tokens.accent.tint,
    border: `1px solid ${tokens.accent.tintStrong}`,
    color: tokens.accent.hover,
    '&:hover': { bgcolor: tokens.accent.tintStrong, border: `1px solid ${tokens.accent.tintStrong}` },
  };

  // `ResponsiveRows` draws these as a grid on `md+` and, below it, stacks each
  // row's cells with a small label on the ones that are not self-describing.
  const planColumns: ResponsiveRowsColumn[] = [
    { key: 'plan', header: 'Plan', width: 'minmax(0,1.6fr)' },
    { key: 'price', header: 'Price', width: '120px', mobileLabel: true },
    { key: 'limits', header: 'Limits', width: 'minmax(0,1.5fr)', mobileLabel: true },
    { key: 'stripe', header: 'Stripe', width: 'minmax(0,1.25fr)', mobileLabel: true },
    { key: 'subscribers', header: 'Stores', width: '120px', mobileLabel: true },
    { key: 'actions', header: '', width: '150px', align: 'end' },
  ];
  const addonColumns: ResponsiveRowsColumn[] = [
    { key: 'addon', header: 'Add-on', width: 'minmax(0,2.4fr)' },
    { key: 'price', header: 'Price', width: '90px' },
    { key: 'stripe', header: 'Stripe', width: 'minmax(0,1.2fr)' },
    { key: 'actions', header: '', width: '160px', align: 'end' },
  ];

  const renderIssueRow = (key: string, text: string, buttonLabel: string, onClick: () => void) => (
    <Stack
      key={key}
      direction="row"
      spacing={1.5}
      alignItems="center"
      justifyContent="space-between"
      sx={{
        bgcolor: tokens.surface.sunken,
        border: `1px solid ${tokens.line.hairline}`,
        borderRadius: '9px',
        p: '8px 10px 8px 24px',
      }}
    >
      <Typography sx={{ fontSize: 13, color: tokens.ink.body }}>{text}</Typography>
      <Button
        size="small"
        onClick={onClick}
        sx={{
          ...softBlueButtonSx,
          height: 30,
          borderRadius: '7px',
          fontSize: 12.5,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {buttonLabel}
      </Button>
    </Stack>
  );

  const isRowActive = (plan: BillingPlan) => drawer?.mode === 'plan' && drawer.planId === plan.id;

  // The plan/add-on the drawer is currently open for, read live from the
  // query data — so the issues card and the row pills (computed above from
  // `plans`) and the drawer itself update together after a save.
  const drawerPlan = drawer?.mode === 'plan' ? (plans || []).find(p => p.id === drawer.planId) || null : null;
  const drawerAddon: AddonDrawerSummary | null =
    drawer?.mode === 'addon'
      ? addonRows.find(r => (r.target.kind === 'addon' ? r.target.key : 'integration') === drawer.addonId) || null
      : null;
  const drawerHasStripeIssue = drawerPlan
    ? paidPlansMissingPrice.some(p => p.id === drawerPlan.id) || plansMissingSetupFeePrice.some(p => p.id === drawerPlan.id)
    : false;

  return (
    <PageContainer>
      <PageHeader
        title="Plans"
        description="Package plans and link Stripe prices. Stripe is the source of truth for what gets charged."
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                refetch();
                subscriptionsQuery.refetch();
                planAddonsQuery.refetch();
                integrationAddonQuery.refetch();
              }}
              disabled={isLoading}
              sx={{ height: 34, borderRadius: '8px', fontSize: 12.5, fontWeight: 600, borderColor: tokens.line.default, color: tokens.ink.body }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Sync />}
              onClick={() => setConfirmSyncOpen(true)}
              disabled={isLoading || syncDefaultsMutation.isPending}
              sx={{ ...softBlueButtonSx, height: 34, borderRadius: '8px', fontSize: 12.5, fontWeight: 600 }}
            >
              {syncDefaultsMutation.isPending ? 'Syncing…' : 'Sync from pricing sheet'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudDownload />}
              onClick={() => stripeImportMutation.mutate(false)}
              disabled={isLoading || stripeImportMutation.isPending}
              sx={{ ...softBlueButtonSx, height: 34, borderRadius: '8px', fontSize: 12.5, fontWeight: 600 }}
            >
              {stripeImportMutation.isPending && !stripeImport ? 'Reading Stripe…' : 'Import from Stripe'}
            </Button>
          </Stack>
        }
      />

      {actionMessage && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
          {actionMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      {/* §1 Stripe issues card — hidden entirely when there is nothing to fix. */}
      {issueCount > 0 && (
        <Box
          sx={{
            bgcolor: tokens.surface.card,
            border: `1px solid ${tokens.line.default}`,
            borderRadius: `${tokens.radius.panel}px`,
            boxShadow: tokens.shadow.card,
            p: '14px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            mb: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberRounded sx={{ fontSize: 16, color: tokens.accent.main }} />
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: tokens.ink.primary }}>
              {issueCount} thing{issueCount === 1 ? '' : 's'} to fix so Stripe charges correctly
            </Typography>
          </Stack>
          {paidPlansMissingPrice.map(plan =>
            renderIssueRow(
              `price-${plan.id}`,
              `${plan.name} has no monthly Stripe price, so stores can't subscribe to it.`,
              'Link price',
              () => openStripeTab(plan),
            ),
          )}
          {plansMissingSetupFeePrice.map(plan =>
            renderIssueRow(
              `setup-${plan.id}`,
              `${plan.name}'s $${(plan.setupFee || 0).toLocaleString('en-US')} setup fee has no Stripe price, so new subscribers won't be charged for it.`,
              'Link setup fee',
              () => openStripeTab(plan),
            ),
          )}
          {addonsMissingPriceCount > 0 &&
            renderIssueRow(
              'addons',
              `${addonsMissingPriceCount} add-ons have a sheet price but no Stripe price, so stores can't buy them yet.`,
              'Review add-ons',
              scrollToAddons,
            )}
        </Box>
      )}

      {/* §2 Plans card */}
      <SectionCard dense sx={{ mb: 2 }}>
        <ResponsiveRows
          columns={planColumns}
          rows={plans || []}
          getRowKey={plan => plan.id}
          isLoading={isLoading}
          skeletonRows={4}
          emptyState={<Typography color="text.secondary">No billing plans found.</Typography>}
          rowSx={plan => ({ bgcolor: isRowActive(plan) ? tokens.signal.info.row : 'transparent' })}
          renderCell={(plan, column) => {
            switch (column) {
              case 'plan':
                return (
                  <>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontSize: 14.5, fontWeight: 700 }}>{plan.name}</Typography>
                      {popularPlanId === plan.id && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            borderRadius: `${tokens.radius.pill}px`,
                            px: '8px',
                            py: '2px',
                            bgcolor: tokens.accent.tint,
                            color: tokens.accent.hover,
                          }}
                        >
                          Popular
                        </Box>
                      )}
                      {plan.isActive === false && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            borderRadius: `${tokens.radius.pill}px`,
                            px: '8px',
                            py: '2px',
                            bgcolor: tokens.surface.tint,
                            color: tokens.ink.bodyAlt,
                          }}
                        >
                          Hidden
                        </Box>
                      )}
                    </Stack>
                    {plan.whoItsFor && (
                      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{plan.whoItsFor}</Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: tokens.ink.faint }}>
                      {(plan.features || []).length} features
                    </Typography>
                  </>
                );
              case 'price':
                return (
                  <>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {isFreeDisplay(plan) ? 'Free' : formatMoney(plan)}
                      {!isFreeDisplay(plan) && (
                        <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: tokens.ink.muted }}>
                          {' '}
                          {cadenceSuffix(plan.interval)}
                        </Box>
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{setupFeeSubLine(plan)}</Typography>
                  </>
                );
              case 'limits':
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {limitChipsFor(plan).map(chip => (
                      <Box
                        key={chip.label}
                        sx={{
                          bgcolor: tokens.surface.sunken,
                          border: `1px solid ${tokens.line.hairline}`,
                          borderRadius: `${tokens.radius.chip}px`,
                          px: '8px',
                          py: '3px',
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Box component="span" sx={{ color: tokens.ink.muted }}>{chip.label}: </Box>
                        <Box component="span" sx={{ color: tokens.ink.primary, fontWeight: 700 }}>{chip.value}</Box>
                      </Box>
                    ))}
                  </Box>
                );
              case 'stripe': {
                const stripeStatus = stripeStatusFor(plan);
                const setupPill = setupFeePillFor(plan);
                return (
                  <Stack spacing={0.5} alignItems="flex-start">
                    <StatusChip label={stripeStatus.label} tone={stripeStatus.tone} />
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        color: tokens.ink.muted,
                        fontFamily: stripeStatus.label === 'Linked' ? tokens.font.mono : undefined,
                      }}
                    >
                      {stripeStatus.note}
                    </Typography>
                    {linkedRegionalPrices(plan).map(({ region, stripePriceId }) => (
                      <Box key={region} sx={{ pt: 0.25 }}>
                        <StatusChip label={`${region}: Linked`} tone="positive" />
                        <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted, fontFamily: tokens.font.mono }}>
                          {shortPriceId(stripePriceId)}
                        </Typography>
                      </Box>
                    ))}
                    {setupPill && <StatusChip label={setupPill.label} tone={setupPill.tone} />}
                  </Stack>
                );
              }
              case 'subscribers': {
                const storeCount = storeCountFor(plan.id);
                const recordCount = subscribersFor(plan.id).length;
                return recordCount > 0 ? (
                  <Button
                    onClick={() => openSubscribersTab(plan)}
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: storeCount > 0 ? tokens.accent.main : tokens.ink.faint,
                      p: 0,
                      minWidth: 0,
                      textTransform: 'none',
                    }}
                  >
                    {storeCount > 0 ? `${storeCount} store${storeCount === 1 ? '' : 's'}` : 'None current'}
                  </Button>
                ) : (
                  <Typography sx={{ fontSize: 13, color: tokens.ink.faint }}>None</Typography>
                );
              }
              case 'actions':
                return (
                  <Stack direction="row" spacing={0.75} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => openPackaging(plan)}
                      sx={{
                        height: 32,
                        borderRadius: '8px',
                        fontSize: 12.5,
                        fontWeight: 600,
                        borderColor: tokens.line.default,
                        color: tokens.ink.body,
                        '&:hover': { bgcolor: tokens.surface.sunken },
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<KeyboardArrowDown fontSize="small" />}
                      onClick={e => setMenuAnchor({ plan, el: e.currentTarget })}
                      sx={{
                        height: 32,
                        borderRadius: '8px',
                        fontSize: 12.5,
                        fontWeight: 600,
                        borderColor: tokens.line.default,
                        color: tokens.ink.body,
                        '&:hover': { bgcolor: tokens.surface.sunken },
                      }}
                    >
                      More
                    </Button>
                  </Stack>
                );
              default:
                return null;
            }
          }}
        />
      </SectionCard>

      {/* More menu — a single instance shared across rows, anchored per click. */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { width: 240, borderRadius: `${tokens.radius.control}px`, boxShadow: tokens.shadow.menu, p: '6px' } }}
      >
        {menuAnchor && !isFreePreviewPlan(menuAnchor.plan) && (
          <MenuItem
            onClick={() => {
              openStripeTab(menuAnchor.plan);
              setMenuAnchor(null);
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {menuAnchor.plan.stripePriceId ? 'Change Stripe price' : 'Link Stripe price'}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint }}>Pick the monthly price Stripe charges</Typography>
            </Box>
          </MenuItem>
        )}
        {menuAnchor && menuAnchor.plan.stripePriceId && (
          <MenuItem
            onClick={() => {
              syncStripeMutation.mutate(menuAnchor.plan);
              setMenuAnchor(null);
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Sync display price from Stripe</Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint }}>Copy the Stripe amount onto the pricing page</Typography>
            </Box>
          </MenuItem>
        )}
        {menuAnchor && (
          <MenuItem
            onClick={() => {
              openSubscribersTab(menuAnchor.plan);
              setMenuAnchor(null);
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>View subscribers</Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint }}>
                {storeCountFor(menuAnchor.plan.id)} store{storeCountFor(menuAnchor.plan.id) === 1 ? '' : 's'} on this plan
              </Typography>
            </Box>
          </MenuItem>
        )}
        {menuAnchor && menuAnchor.plan.stripePriceId && (
          <MenuItem
            onClick={() => {
              setUnlinkPlan(menuAnchor.plan);
              setUnlinkRegion(null);
              setUnlinkError(null);
              setMenuAnchor(null);
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.signal.caution.ink }}>Unlink Stripe price</Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint }}>New stores won&apos;t be able to subscribe</Typography>
            </Box>
          </MenuItem>
        )}
        {menuAnchor &&
          linkedRegionalPrices(menuAnchor.plan).map(({ region }) => (
            <MenuItem
              key={region}
              onClick={() => {
                setUnlinkPlan(menuAnchor.plan);
                setUnlinkRegion(region);
                setUnlinkError(null);
                setMenuAnchor(null);
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.signal.caution.ink }}>
                  Unlink {region} Stripe price
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint }}>
                  New {region} stores fall back to the default price
                </Typography>
              </Box>
            </MenuItem>
          ))}
        {menuAnchor && (
          /*
           * Deleting a live plan stays a two-step: deactivate, then delete.
           * The backend only refuses while a subscription still points at
           * the plan, so without this an offered plan with no subscribers
           * yet is one click and one confirm from permanent deletion.
           */
          <MenuItem
            disabled={menuAnchor.plan.isActive || deletePlanMutation.isPending}
            onClick={() => {
              setDeletePlan(menuAnchor.plan);
              setMenuAnchor(null);
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.signal.problem.ink }}>Delete plan</Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint }}>
                {menuAnchor.plan.isActive
                  ? 'Turn off "Offered to stores" first'
                  : 'Move subscribers first / Removes it permanently'}
              </Typography>
            </Box>
          </MenuItem>
        )}
      </Menu>

      {/* §3 À la carte add-ons card */}
      <Box ref={addonsCardRef} sx={{ scrollMarginTop: '16px' }}>
        <SectionCard dense title="À la carte add-ons" subtitle="Stores can only buy an add-on after its Stripe price is linked.">
          {(planAddonsQuery.isError || integrationAddonQuery.isError) && (
            <Box sx={{ p: '12px 20px 0' }}>
              <InlineMessage severity="error">
                {(planAddonsQuery.error as Error)?.message || (integrationAddonQuery.error as Error)?.message}
              </InlineMessage>
            </Box>
          )}
          {(planAddonsQuery.isLoading || integrationAddonQuery.isLoading) && (
            <Box sx={{ p: '16px 20px' }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height={48} sx={{ mb: 1 }} />
              ))}
            </Box>
          )}
          {!planAddonsQuery.isLoading && !integrationAddonQuery.isLoading && addonGroups.length === 0 && (
            <Box sx={{ p: '16px 20px' }}>
              <Typography color="text.secondary">No add-ons configured.</Typography>
            </Box>
          )}
          {addonGroups.map(group => (
            <Box key={group.key}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ bgcolor: tokens.surface.sunken, borderTop: `1px solid ${tokens.line.hairline}`, px: '20px', pt: '14px', pb: '8px' }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: group.dot, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{group.title}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.ink.muted }}>{group.rows.length}</Typography>
                <Typography sx={{ fontSize: 12, color: tokens.ink.faint }}>· {group.note}</Typography>
              </Stack>
              <ResponsiveRows
                columns={addonColumns}
                rows={group.rows}
                getRowKey={row => (row.target.kind === 'addon' ? row.target.key : 'integration')}
                showHeader={false}
                gap={{ xs: '10px', md: '16px' }}
                padding="12px 20px"
                renderCell={(row, column) => {
                  switch (column) {
                    case 'addon':
                      return (
                        <>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{row.name}</Typography>
                            {row.partiallyLive && (
                              <Box
                                component="span"
                                sx={{ fontSize: 11, fontWeight: 700, borderRadius: `${tokens.radius.pill}px`, px: '8px', py: '2px', bgcolor: tokens.accent.tint, color: tokens.accent.hover }}
                              >
                                Partially live
                              </Box>
                            )}
                          </Stack>
                          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{row.description}</Typography>
                          <Typography sx={{ fontSize: 12, color: tokens.ink.faint }}>{availabilitySentence(row)}</Typography>
                          {/* PLAN_ADDONS enforces "unmapped means comingSoon" (planAddonComingSoon.test.ts),
                              so this can't fire for a shipped add-on today — but it's the literal backend
                              rule (`Boolean(unlocks || unlocksLevel)`), so a row that ever violates that
                              invariant still reads correctly here instead of silently as "just needs a price". */}
                          {!row.mapped && !row.comingSoon && (
                            <Typography sx={{ fontSize: 12, color: tokens.signal.caution.solid }}>
                              Not mapped to a capability — can&apos;t be sold even once priced.
                            </Typography>
                          )}
                        </>
                      );
                    case 'price':
                      return (
                        <Typography sx={{ fontSize: 13 }}>
                          {formatAddonMoney(row.price, row.currency)}
                          <Box component="span" sx={{ color: tokens.ink.muted }}> / {row.interval === 'yearly' ? 'yr' : 'mo'}</Box>
                        </Typography>
                      );
                    case 'stripe':
                      return (
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            fontFamily: row.stripePriceId ? tokens.font.mono : undefined,
                            color: row.stripePriceId ? tokens.ink.muted : tokens.ink.faint,
                          }}
                        >
                          {row.stripePriceId ? shortPriceId(row.stripePriceId) : 'No Stripe price'}
                        </Typography>
                      );
                    case 'actions':
                      /* The per-row unlink icon moves into the drawer (§3):
                         choosing "Not linked" in the Stripe price dropdown
                         there and saving, which still runs behind a confirm —
                         see PlanEditorDrawer's `addonUnlinkConfirmOpen`. */
                      return (
                        <Button
                          variant={group.key === 'needs' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => openAddonDrawer(row.target)}
                          sx={
                            group.key === 'needs'
                              ? { bgcolor: tokens.accent.main, color: tokens.ink.inverse, '&:hover': { bgcolor: tokens.accent.hover } }
                              : { borderColor: tokens.line.default, color: tokens.ink.body }
                          }
                        >
                          {row.stripePriceId ? 'Change price' : 'Link Stripe price'}
                        </Button>
                      );
                    default:
                      return null;
                  }
                }}
              />
            </Box>
          ))}
        </SectionCard>
      </Box>

      <PlanEditorDrawer
        open={!!drawer}
        mode={drawer?.mode || null}
        requestedTab={drawer?.tab || 'packaging'}
        plan={drawerPlan}
        addon={drawerAddon}
        subscribers={drawerPlan ? subscribersFor(drawerPlan.id) : []}
        hasStripeIssue={drawerHasStripeIssue}
        onClose={() => setDrawer(null)}
        onSaved={message => {
          setActionMessage(message);
          setDrawer(null);
        }}
        onOpenMoveSubscribers={plan => {
          setMoveNotice(null);
          setMoveTo('');
          setMoveFrom(plan);
        }}
      />

      <ConfirmDialog
        open={!!unlinkPlan}
        title={
          unlinkRegion
            ? `Unlink the ${unlinkRegion} Stripe price for "${unlinkPlan?.name}"?`
            : `Unlink the default Stripe price for "${unlinkPlan?.name}"?`
        }
        message={
          <>
            {unlinkRegion ? (
              <>
                New {unlinkRegion} stores will be charged the plan&apos;s default price instead. Existing{' '}
                {unlinkRegion} subscribers already billed by Stripe are unaffected.
              </>
            ) : (
              <>
                New stores won&apos;t be able to subscribe until another price is linked. Existing subscribers already
                billed by Stripe are unaffected.
              </>
            )}
            {unlinkError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {unlinkError}
              </Alert>
            )}
          </>
        }
        confirmLabel="Unlink price"
        confirmColor="warning"
        loading={unlinkStripeMutation.isPending}
        onConfirm={() => {
          // Stay open until the mutation settles: closing here (the old
          // behavior) hid the `loading` spinner and dropped a failure into
          // the page alert after the dialog was already gone.
          if (unlinkPlan) unlinkStripeMutation.mutate({ plan: unlinkPlan, region: unlinkRegion });
        }}
        onClose={() => {
          setUnlinkPlan(null);
          setUnlinkRegion(null);
          setUnlinkError(null);
        }}
      />

      <ConfirmDialog
        open={confirmSyncOpen}
        maxWidth="sm"
        title={syncPreview ? `Sync plans on the ${stackLabel(syncPreview.stack)}?` : 'Sync plans from the pricing sheet?'}
        message={
          'Plan names, descriptions, features, limits, prices and setup fees are rewritten from the pricing sheet ' +
          'defaults. Stripe links, regional prices, the popular flag and each plan\'s own "Offered to stores" ' +
          'setting are kept. Custom plans are left alone.'
        }
        preview={
          <>
            <PlanSyncPreview
              preview={syncPreview}
              loading={syncPreviewQuery.isFetching}
              error={syncPreviewQuery.error ? (syncPreviewQuery.error as Error).message : null}
              onRetry={() => syncPreviewQuery.refetch()}
            />
            {syncDefaultsMutation.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {(syncDefaultsMutation.error as Error).message}
              </Alert>
            )}
          </>
        }
        confirmLabel={
          syncPreview && syncPreview.changeCount > 0
            ? `Apply ${syncPreview.changeCount} change${syncPreview.changeCount === 1 ? '' : 's'}`
            : 'Sync plans'
        }
        confirmColor="warning"
        confirmDisabled={!syncPreview || syncPreview.changeCount === 0}
        loading={syncDefaultsMutation.isPending}
        onConfirm={() => syncDefaultsMutation.mutate()}
        onClose={() => {
          setConfirmSyncOpen(false);
          syncDefaultsMutation.reset();
          queryClient.removeQueries({ queryKey: ['admin-plan-sync-preview'] });
        }}
      />
      <ConfirmDialog
        open={!!deletePlan}
        title={`Delete "${deletePlan?.name}"?`}
        message="The plan doc is removed from Motzu. Its Stripe product and prices are untouched. This is refused while any subscription still points at the plan."
        confirmLabel="Delete plan"
        confirmColor="error"
        loading={deletePlanMutation.isPending}
        onConfirm={() => deletePlan && deletePlanMutation.mutate(deletePlan)}
        onClose={() => setDeletePlan(null)}
      />

      <FormDialog
        open={!!moveFrom}
        onClose={() => setMoveFrom(null)}
        onSubmit={() => moveSubscriptionsMutation.mutate()}
        title={`Move subscribers off "${moveFrom?.name}"`}
        submitLabel={moveSubscriptionsMutation.isPending ? 'Moving…' : 'Move subscribers'}
        submitColor="warning"
        submitDisabled={!moveTo || moveSubscriptionsMutation.isPending}
        maxWidth="sm"
      >
          {moveNotice && <Alert severity="warning" sx={{ mb: 2 }}>{moveNotice}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Motzu-side subscriptions are pointed at the plan you choose. Subscriptions still billed by Stripe are left
            alone and listed, since their price lives in Stripe; change or cancel those in Stripe first. Then delete the plan.
          </Typography>
          <TextField
            select
            label="Move to plan"
            value={moveTo}
            onChange={e => setMoveTo(e.target.value)}
            fullWidth
          >
            {(plans || [])
              .filter(p => p.id !== moveFrom?.id && p.isActive)
              .map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} — {formatMoney(p)} / {p.interval}
                </MenuItem>
              ))}
          </TextField>
      </FormDialog>

      {/* Stripe import preview */}
      <Dialog
        open={!!stripeImport}
        onClose={() => setStripeImport(null)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>{stripeImport?.applied ? 'Stripe prices linked' : 'Import prices from Stripe'}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Active Stripe prices are matched to plans by their product&apos;s <code>motzu_key</code> metadata or by name.
            Monthly USD prices become the plan price, CAD the CA regional price, and one-time prices on a
            &quot;Setup Fee&quot; product the setup fee. Display prices are refreshed from Stripe on apply.
          </Alert>
          {stripeImport && stripeImport.rows.length === 0 && (
            <Typography color="text.secondary">No Stripe prices matched a Motzu plan.</Typography>
          )}
          {stripeImport && stripeImport.rows.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Stripe product</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Lands on</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stripeImport.rows.map(row => (
                    <TableRow key={row.stripePriceId}>
                      <TableCell>
                        {row.productName}
                        <Typography variant="caption" display="block" sx={{ fontFamily: tokens.font.mono }}>
                          {row.stripePriceId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.amount.toLocaleString('en-US', { style: 'currency', currency: row.currency, maximumFractionDigits: 0 })}
                        {row.kind === 'setup_fee' ? ' one-time' : row.kind === 'plan' ? ' / mo' : ' / mo add-on'}
                      </TableCell>
                      <TableCell>{row.planName || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: tokens.font.mono, fontSize: '0.8rem' }}>{row.target}</TableCell>
                      <TableCell>
                        {row.note ? (
                          <Tooltip title={row.note}><Chip label="Reference only" size="small" /></Tooltip>
                        ) : row.applied ? (
                          <Chip label="Linked" size="small" color="success" />
                        ) : (
                          <Chip label="Will link" size="small" color="primary" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {stripeImport && stripeImport.unmatched.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Skipped</Typography>
              {stripeImport.unmatched.map(item => (
                <Typography key={item.stripePriceId} variant="body2" color="text.secondary">
                  {item.productName} ({item.stripePriceId}): {item.reason}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStripeImport(null)}>{stripeImport?.applied ? 'Close' : 'Cancel'}</Button>
          {stripeImport && !stripeImport.applied && stripeImport.rows.some(r => r.planId) && (
            <Button
              variant="contained"
              onClick={() => stripeImportMutation.mutate(true)}
              disabled={stripeImportMutation.isPending}
            >
              {stripeImportMutation.isPending ? 'Linking…' : 'Link these prices'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AdminPlansPage;
